import {
    createCategoryConfig as createCategoryConfigApi,
    queryCategoryConfig as queryCategoryConfigApi,
    queryExpenseCategoryConfig as queryExpenseCategoryConfigApi,
    translateText as translateTextApi,
} from "./SpatApi";
import { showErrorToast } from "../utils/Toasts";
import { getDataFromIndexDB, storeDataToIndexDB, clearDataFromIndexDB } from "../utils/dataStore";

const CACHE_KEY_queryCategoryConfig = "CACHE_queryCategoryConfig";
const CACHE_KEY_queryExpenseCategoryConfig = 'CACHE_KEY_queryExpenseCategoryConfig';
const TRANSLATION_CACHE_KEY = "CACHE_translationCacheV1";

// Utility to skip unnecessary translations
const isSkippableToken = (text) => {
    return (
        text.length === 1 ||
        /^[0-9]+$/.test(text) ||           // Numbers only
        /^[\W_]+$/.test(text)              // Special characters only
    );
};

// Optional: Detect Tamil script to avoid double translation
const isTamil = (text) => /^[\u0B80-\u0BFF\s]+$/.test(text);

// Read cache from localStorage
const getCache = async () => {
    try {
        const raw = await getDataFromIndexDB(TRANSLATION_CACHE_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
};

// Save cache to localStorage
const setCache = async (cache) => {
    try {
        await storeDataToIndexDB(TRANSLATION_CACHE_KEY, JSON.stringify(cache), Number(process.env.REACT_APP_TRANSLATION_CACHE_EXPIRATION));
    } catch (err) {
        console.warn("Failed to save translation cache:", err);
    }
};

// Clean old cache entries
const cleanCache = (cache) => {
    const now = Date.now();
    for (const key in cache) {
        if (now - cache[key].timestamp > Number(process.env.REACT_APP_TRANSLATION_CACHE_EXPIRATION)) {
            delete cache[key];
        }
    }
    return cache;
};

export async function translateText(data) {
    try {
        if (
            !data ||
            data.trim() === "" ||
            data.toLowerCase() === "na" ||
            isSkippableToken(data) ||
            isTamil(data) // Optional: skip if already Tamil
        ) {
            return data; // Return original input without API call
        }
        const cache = cleanCache(await getCache());
        // 🔍 Check exact key match
        const cachedEntry = cache[data];
        if (cachedEntry?.translated) {
            return cachedEntry.translated;
        }
        const jsonData = JSON.stringify({ translateText: data });
        const response = await translateTextApi(jsonData);

        if (response?.status === 201 || response?.status === 200) {
            const translated = response.data.translateTextResponse.translatedText;

            // Save to cache
            cache[data] = {
                translated,
                timestamp: Date.now(),
            };
            await setCache(cache);

            return translated;
        } else {
            showErrorToast(response?.data?.message || response?.message || "Something went wrong while translating data");
            return false;
        }
    } catch (error) {
        showErrorToast(error?.message || "Something went wrong during translation.");
        return false;
    }
}

export async function createCategoryConfig(data) {
    try {
        const response = await createCategoryConfigApi(data);
        if (response?.status === 201 || response?.status === 200) {
            await clearDataFromIndexDB(CACHE_KEY_queryCategoryConfig);
            return true;
        } else {
            showErrorToast(response?.data?.message || response?.message || "Something went wrong while creating category data");
            return false;
        }
    } catch (error) {
        showErrorToast(error?.message || "Something went wrong during category creation.");
        return false;
    }
}

export async function queryCategoryConfig(data) {
    try {
        const cachedData = JSON.parse(await getDataFromIndexDB(CACHE_KEY_queryCategoryConfig));
        if (cachedData) {
            return cachedData;
        }
        const response = await queryCategoryConfigApi(data);
        if (response?.status === 201 || response?.status === 200) {
            await storeDataToIndexDB(CACHE_KEY_queryCategoryConfig, JSON.stringify(response.data), Number(process.env.REACT_APP_GENERAL_CACHE_EXPIRY_TIME));
            return response.data;
        } else {
            showErrorToast(response?.data?.message || response?.message || "Something went wrong while querying category data");
            return false;
        }
    } catch (error) {
        showErrorToast(error?.message || "Something went wrong during category query.");
        return false;
    }
}

export async function queryExpenseCategoryConfig(data) {
    try {
        const cachedData = JSON.parse(await getDataFromIndexDB(CACHE_KEY_queryExpenseCategoryConfig));
        if (cachedData) {
            return cachedData;
        }
        const response = await queryExpenseCategoryConfigApi(data);
        if (response?.status === 201 || response?.status === 200) {
            await storeDataToIndexDB(CACHE_KEY_queryExpenseCategoryConfig, JSON.stringify(response.data), Number(process.env.REACT_APP_GENERAL_CACHE_EXPIRY_TIME));
            return response.data;
        } else {
            showErrorToast(response?.data?.message || response?.message || "Something went wrong while querying category data");
            return false;
        }
    } catch (error) {
        showErrorToast(error?.message || "Something went wrong during category query.");
        return false;
    }
}